import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Search } from 'react-feather';
import { toast } from 'react-toastify';
import { Page, PageHeader, Card, Button, Field, TextInput, Select, Toolbar, Table, Loader, EmptyState } from '../../../../ui';
import './teacherSlotsAvailable.scss';
import { ConfigContext } from '../../../../Api/global/ApiContext';
import { getTeacherFreeSlotCalendar } from '../../../../Api/apiUtils';

interface Teacher {
  teacherId: number;
  teacherName: string;
  payoutCategory?: string | null;
  teacherTier?: string | null;
  tier?: string | null;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  teachers: Teacher[];
}

interface Day {
  dayName: string;
  dayOfWeek: number;
  timeSlots: TimeSlot[];
}

interface Course {
  courseId: number;
  courseName: string;
  days: Day[];
}

type Option = { value: string; label: string };
const TIER_OPTIONS: Option[] = [
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'platinum', label: 'Platinum' },
];

const getTodayLocalDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeTier = (raw: string): 'silver' | 'gold' | 'diamond' | 'platinum' | null => {
  const k = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (k.includes('silver')) return 'silver';
  if (k.includes('gold')) return 'gold';
  if (k.includes('diamond')) return 'diamond';
  if (k.includes('platinum')) return 'platinum';
  return null;
};

const getTeacherTierRaw = (teacher: Teacher): string => {
  const t = teacher as unknown as Record<string, unknown>;
  const candidates = [
    teacher.payoutCategory,
    teacher.teacherTier,
    teacher.tier,
    t.payout_category,
    t.teacherTier,
    t.teacher_tier,
  ];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c);
  }
  return '';
};

type TierKey = 'silver' | 'gold' | 'diamond' | 'platinum' | 'other';

/** Chip modifier for a teacher's payout tier; colours live in teacherSlotsAvailable.scss. */
const getTeacherTierKey = (teacher: Teacher): TierKey => normalizeTier(getTeacherTierRaw(teacher)) ?? 'other';

const TeacherSlotsAvailable = () => {
  const config = useContext(ConfigContext);
  const { ageGroups = [], grmpcourses = [] } = config || {};

  const [data, setData] = useState<Course[]>([]);
  const [fromDate, setFromDate] = useState(getTodayLocalDate());
  const [consecutiveWeeks, setConsecutiveWeeks] = useState(4);
  const [weeksInput, setWeeksInput] = useState('4');
  const [loading, setLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<string>('silver');
  const initialFetchDoneRef = useRef(false);

  const courseOptions = useMemo(() => {
    return (grmpcourses as { name?: string; v2Id?: string | number }[])
      .filter((course) => {
        const name = course.name?.trim() ?? '';
        if (!name || name.toLowerCase().includes('offline')) return false;
        return course.v2Id != null && String(course.v2Id).trim() !== '';
      })
      .map((course) => ({
        value: String(course.v2Id),
        label: course.name!.trim(),
      }));
  }, [grmpcourses]);

  const ageGroupOptions = useMemo(
    () => ageGroups.map((ag: { id: number; name: string }) => ({ value: String(ag.id), label: ag.name })),
    [ageGroups],
  );

  const fetchSlots = async (courseIdValue: string = selectedCourseId, ageGroupIdValue: string = selectedAgeGroupId) => {
    setLoading(true);
    try {
      const courseId = parseInt(courseIdValue, 10);
      const ageGroupId = parseInt(ageGroupIdValue, 10);

      const payload = {
        fromDate,
        consecutiveWeeks,
        ...(!Number.isNaN(courseId) ? { courseIds: [courseId] } : {}),
        ...(!Number.isNaN(ageGroupId) ? { ageGroupIds: [ageGroupId] } : {}),
      };

      const response = await getTeacherFreeSlotCalendar(payload);
      const list = Array.isArray(response) ? response : [];
      setData(list);
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Could not load free-slot calendar. Check dates, filters, and login.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedCourseId && courseOptions.length > 0) {
      setSelectedCourseId(courseOptions[0].value);
    }
  }, [courseOptions, selectedCourseId]);

  useEffect(() => {
    if (!selectedCourseId || initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    fetchSlots(selectedCourseId, selectedAgeGroupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  const timeSlots = data.flatMap((course) => course.days.flatMap((day) => day.timeSlots));
  const uniqueTimes = Array.from(new Set(timeSlots.map((slot) => slot.startTime))).sort();

  const getTeachersForCourseDayAndTime = (course: Course, day: Day, time: string): Teacher[] => {
    const slot = day.timeSlots.find((s) => s.startTime === time);
    if (!slot) return [];
    if (!selectedTier) return slot.teachers;
    return slot.teachers.filter((teacher) => normalizeTier(getTeacherTierRaw(teacher) || '') === selectedTier);
  };
  const coursesToShow = data;

  const formatTime = (timeString: string) =>
    new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  const parseWeeksInput = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed < 1) return null;
    return parsed;
  };

  const ageGroupSelectOptions: Option[] = [{ value: '', label: 'All age groups' }, ...ageGroupOptions];
  const tierSelectOptions: Option[] = [{ value: '', label: 'All levels' }, ...TIER_OPTIONS];
  const courseSelectOptions: Option[] =
    courseOptions.length === 0 ? [{ value: '', label: 'No courses available' }] : courseOptions;

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="Slots Available"
        subtitle="Teachers free in each weekly slot, by course, age group and level."
      />

      <Card>
        <Toolbar>
          <Field label="From">
            {(ctl) => <TextInput {...ctl} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />}
          </Field>
          <Field label="Weeks" className="slots-filter--narrow">
            {(ctl) => (
              <TextInput
                {...ctl}
                type="number"
                numeric
                min={1}
                value={weeksInput}
                onChange={(e) => {
                  const next = e.target.value;
                  if (/^\d*$/.test(next)) {
                    setWeeksInput(next);
                    const parsedWeeks = parseWeeksInput(next);
                    if (parsedWeeks != null) {
                      setConsecutiveWeeks(parsedWeeks);
                    }
                  }
                }}
              />
            )}
          </Field>
          <Field label="Course" className="ds-toolbar__grow">
            {(ctl) => (
              <Select
                {...ctl}
                options={courseSelectOptions}
                value={selectedCourseId}
                onChange={(e) => {
                  const nextCourseId = e.target.value;
                  setSelectedCourseId(nextCourseId);
                  setSelectedTier('silver');
                  fetchSlots(nextCourseId, selectedAgeGroupId);
                }}
              />
            )}
          </Field>
          <Field label="Age group">
            {(ctl) => (
              <Select
                {...ctl}
                options={ageGroupSelectOptions}
                value={selectedAgeGroupId}
                onChange={(e) => setSelectedAgeGroupId(e.target.value)}
              />
            )}
          </Field>
          <Field label="Level">
            {(ctl) => (
              <Select {...ctl} options={tierSelectOptions} value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)} />
            )}
          </Field>
          <div className="ds-toolbar__end">
            <Button
              variant="primary"
              icon={<Search size={16} aria-hidden />}
              loading={loading}
              onClick={() => {
                const parsedWeeks = parseWeeksInput(weeksInput);
                if (parsedWeeks == null) {
                  toast.error('Week count is mandatory.');
                  return;
                }
                setConsecutiveWeeks(parsedWeeks);
                fetchSlots();
              }}
            >
              Fetch slots
            </Button>
          </div>
        </Toolbar>

        <ul className="slots-legend" aria-label="Teacher levels">
          {TIER_OPTIONS.map((tier) => (
            <li key={tier.value} className={`slots-chip slots-chip--${tier.value}`}>
              {tier.label}
            </li>
          ))}
        </ul>
      </Card>

      {loading ? (
        <Loader label="Loading free slots…" />
      ) : coursesToShow.length === 0 ? (
        <EmptyState
          title="No free slots"
          description="No slots came back for these filters. Try another course or age group, an earlier start date, or more weeks."
        />
      ) : (
        <Card title="Free teachers by day and time" flush>
          <Table caption="Free teachers by day and time" stickyHeader wrapClassName="slots-grid-wrap" className="slots-grid">
            <thead>
              <tr>
                <th scope="col">Day</th>
                {uniqueTimes.map((time) => (
                  <th key={time} scope="col" className="slots-grid__time">
                    {formatTime(time)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {coursesToShow.map((course) =>
                course.days.map((day) => (
                  <tr key={`${course.courseId}-${day.dayName}`}>
                    <th scope="row" className="slots-grid__day">
                      {day.dayName}
                    </th>
                    {uniqueTimes.map((time) => {
                      const teachers = getTeachersForCourseDayAndTime(course, day, time);
                      return (
                        <td key={time} className="slots-grid__cell">
                          {teachers.length > 0 ? (
                            <ul className="slots-grid__teachers">
                              {teachers.map((teacher, index) => (
                                <li
                                  key={`${teacher.teacherId}-${time}-${index}`}
                                  className={`slots-chip slots-chip--${getTeacherTierKey(teacher)}`}
                                >
                                  {teacher.teacherName}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="ds-text-subtle">–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )),
              )}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
};

export default TeacherSlotsAvailable;
